/**
 * Shared freeTextInput gate for ConfirmDialog + ConfirmDialogInline.
 *
 * freeTextInput may include:
 *   requireChange – trimmed must differ from initialValue
 *   validate(trimmed) → { ok: boolean, helper?: string, tone?: 'muted'|'danger' }
 *
 * `validate` is how Apply-credit (and future money confirms) soft-block
 * over-max / invalid amounts and surface a live helper (e.g. remaining due).
 * Returns { ok, helper, tone }. ok=false disables confirm.
 */
export function evaluateConfirmFreeText(freeTextInput, trimmed) {
  if (!freeTextInput) return { ok: true, helper: null, tone: null };
  const text = String(trimmed ?? '').trim();
  if (!text) return { ok: false, helper: null, tone: null };

  if (freeTextInput.requireChange) {
    const initialTrim = (freeTextInput.initialValue ?? '').trim();
    if (text === initialTrim) return { ok: false, helper: null, tone: null };
  }

  // `minLength` was accepted by callers and silently ignored here, so
  // `useInvoiceDateGuard`'s reason-required tier — the one that exists purely
  // to capture a real explanation for the audit trail — accepted a
  // single-character reason.
  if (Number.isFinite(freeTextInput.minLength) && text.length < freeTextInput.minLength) {
    return { ok: false, helper: null, tone: null };
  }

  if (typeof freeTextInput.validate === 'function') {
    const result = freeTextInput.validate(text);

    // THREE conventions are in the wild, and only one of them used to work.
    //
    //  · null / undefined  → VALID. `useBackdateGuard` writes it this way, and
    //    the old `result || {}` turned that into `{ ok: false }` — so the
    //    reason-required backdate tier could NEVER be confirmed. A perfectly
    //    valid reason left the button dead with no explanation, the same shape
    //    as the tax modal that once told Spanish owners to type CONFIRMAR and
    //    then refused it.
    //  · a string          → INVALID, and the string is the helper text.
    //  · { ok, helper, tone } → the explicit form (apply-credit).
    if (result == null) return { ok: true, helper: null, tone: null };
    if (typeof result === 'string') {
      return { ok: false, helper: result, tone: 'danger' };
    }
    return {
      ok: Boolean(result.ok),
      helper: result.helper || null,
      tone: result.tone === 'danger' ? 'danger' : (result.helper ? 'muted' : null),
    };
  }

  return { ok: true, helper: null, tone: null };
}
